import * as React from 'react'
import { Icon } from '@blueprintjs/core'
import classNames from 'classnames'

type EmptyStateIcon = React.ReactNode | string

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
    action?: React.ReactNode
    description?: React.ReactNode
    icon?: EmptyStateIcon
    iconSize?: number
    layout?: 'vertical' | 'horizontal'
    title?: React.ReactNode
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
    (
        {
            action,
            children,
            className,
            description,
            icon,
            iconSize = 48,
            layout = 'vertical',
            title,
            ...props
        },
        ref,
    ) => {
        const horizontal = layout === 'horizontal'
        const items = [
            icon != null && {
                key: 'visual',
                node: (
                    <div
                        className="mj-empty-state-visual"
                        style={{ fontSize: iconSize, lineHeight: `${iconSize}px` }}
                    >
                        <Icon icon={icon as any} size={iconSize} aria-hidden tabIndex={-1} />
                    </div>
                ),
            },
            (title || description) && {
                key: 'text',
                node: (
                    <div>
                        {title && (
                            <h4
                                className={classNames(
                                    'm-0 p-0 text-[18px] font-semibold leading-5 text-[#5f6b7c]',
                                    description && 'mb-[10px]',
                                )}
                            >
                                {title}
                            </h4>
                        )}
                        {description && <div>{description}</div>}
                    </div>
                ),
            },
            action && {
                key: 'action',
                node: action,
            },
            children && {
                key: 'children',
                node: children,
            },
        ].filter(Boolean) as { key: string; node: React.ReactNode }[]

        return (
            <>
                <style>
                    {`
                        .mj-empty-state-visual {
                            color: #8f99a8;
                        }
                        .mj-empty-state-visual .bp4-icon svg {
                            fill-opacity: 15%;
                            overflow: visible;
                        }
                        .mj-empty-state-visual .bp4-icon svg path {
                            stroke: #8f99a8;
                            stroke-opacity: 50%;
                            stroke-width: 0.5px;
                        }
                    `}
                </style>
                <div
                    {...props}
                    ref={ref}
                    className={classNames(
                        'flex h-full w-full items-center justify-center text-[#5f6b7c]',
                        horizontal ? 'flex-row text-left' : 'flex-col text-center',
                        className,
                    )}
                >
                    {items.map((item, index) => (
                        <div
                            key={item.key}
                            className={classNames(
                                'max-w-[400px] flex-none',
                                index < items.length - 1 && (horizontal ? 'mr-5' : 'mb-5'),
                                horizontal && 'mb-0',
                            )}
                        >
                            {item.node}
                        </div>
                    ))}
                </div>
            </>
        )
    },
)
EmptyState.displayName = 'EmptyState'

export { EmptyState }
